import React, { useEffect, useState } from "react";
import {
  cancelAccountDeletion,
  getAccountDeletionStatus,
  requestAccountDeletionLink,
  scheduleAccountDeletion,
  type AccountDeletionStatus,
} from "../api/endpoints";
import { useOptionalUser } from "../contexts/UserContext";
import { navigateTo } from "../utils/navigation";
import DeletionShell, { formatDaysLeft, formatDeletionDate } from "../components/account/DeletionShell";

const SUPPORT_EMAIL = "support@kivo.skin";

/**
 * Public account-deletion page, linked from the Google Play Data safety form
 * and the app stores' listings. Deleting is never instant: a request schedules
 * the account for deletion after a redemption period, during which the owner
 * can cancel. Only a verified owner can ask — a signed-in session, or an
 * emailed single-use link (see DeleteAccountConfirmPage).
 */
export default function DeleteAccountPage(): React.JSX.Element {
  const userCtx = useOptionalUser();
  const email = userCtx?.user?.email ?? "";
  const authed = Boolean(userCtx?.authed && email);

  const [status, setStatus] = useState<AccountDeletionStatus | null>(null);
  const [statusFailed, setStatusFailed] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fallback for people who cannot sign in: an emailed single-use link.
  const [linkEmail, setLinkEmail] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkSent, setLinkSent] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const linkEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(linkEmail.trim());

  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    getAccountDeletionStatus()
      .then((s) => { if (!cancelled) setStatus(s); })
      .catch(() => { if (!cancelled) setStatusFailed(true); });
    return () => { cancelled = true; };
  }, [authed]);

  const confirmed =
    email !== "" && confirmation.trim().toLowerCase() === email.toLowerCase();

  const run = async (action: () => Promise<AccountDeletionStatus>, failure: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setStatus(await action());
      setConfirmation("");
    } catch {
      setError(failure);
    } finally {
      setBusy(false);
    }
  };

  const handleSchedule = (): Promise<void> => {
    if (!confirmed || busy) return Promise.resolve();
    return run(
      scheduleAccountDeletion,
      `We couldn't schedule the deletion. Please try again, or email ${SUPPORT_EMAIL} from the address you registered with.`,
    );
  };

  const handleCancel = (): Promise<void> =>
    busy ? Promise.resolve() : run(cancelAccountDeletion, "We couldn't cancel the deletion. Please try again.");

  const handleRequestLink = async (): Promise<void> => {
    if (!linkEmailValid || linkBusy) return;
    setLinkBusy(true);
    setLinkError(null);
    try {
      await requestAccountDeletionLink(linkEmail.trim());
      setLinkSent(linkEmail.trim());
    } catch {
      setLinkError("We couldn't send the email right now. Please wait a moment and try again.");
    } finally {
      setLinkBusy(false);
    }
  };

  const graceDays = status?.grace_days;

  return (
    <DeletionShell>
      <header style={{ marginBottom: "var(--s-6)" }}>
        <p className="t-label" style={{ marginBottom: "var(--s-3)" }}>Account deletion</p>
        <h1 className="t-h2" style={{ margin: "0 0 var(--s-3)" }}>
          Delete your <em className="t-italic t-teal">Kivo Skin</em> account
        </h1>
        <p className="t-small t-mute" style={{ margin: 0 }}>
          This page lets you delete your kivo.skin / Kivo Skin account and its data, without
          reinstalling the app. To protect you from mistakes and misuse, deletion is not instant:
          we delete the account after a waiting period{graceDays ? ` of ${graceDays} days` : ""}, and
          you can cancel at any time before then.
        </p>
      </header>

      <hr className="rule" style={{ marginBottom: "var(--s-6)" }} />

      <section className="stack-3" style={{ marginBottom: "var(--s-6)" }}>
        <h2 className="t-h3" style={{ margin: 0 }}>What we delete</h2>
        <ul className="t-small stack-2" style={{ margin: 0, paddingLeft: "var(--s-5)" }}>
          <li>Your profile, sign-in credentials and preferences.</li>
          <li>Your scan photos and analysis results, including hair and makeup analyses.</li>
          <li>Your favorites, scan history, credits and in-app purchase records.</li>
          <li>Your profile picture and notification settings.</li>
        </ul>
        <h2 className="t-h3" style={{ margin: "var(--s-3) 0 0" }}>What we keep</h2>
        <ul className="t-small stack-2" style={{ margin: 0, paddingLeft: "var(--s-5)" }}>
          <li>
            Records of the policies you accepted (terms, privacy, photo consent), kept as legal
            proof of consent.
          </li>
          <li>
            Apple and Google keep their own purchase records, which we cannot delete. See our{" "}
            <a href="/data-retention" className="link">data retention schedule</a>.
          </li>
        </ul>
        <p className="t-small t-mute" style={{ margin: 0 }}>
          <strong>Subscriptions:</strong> deleting your account does not cancel a subscription
          bought through the App Store or Google Play. Cancel it in your store account settings
          first, or you may keep being billed.
        </p>
      </section>

      <hr className="rule" style={{ marginBottom: "var(--s-6)" }} />

      {!userCtx?.ready ? (
        <p className="t-small t-mute" role="status" style={{ margin: 0 }}>Loading…</p>
      ) : authed ? (
        status?.scheduled ? (
          <section className="stack-4" role="status">
            <h2 className="t-h3" style={{ margin: 0 }}>Your account is scheduled for deletion</h2>
            <p className="t-small" style={{ margin: 0 }}>
              It will be permanently deleted on <strong>{formatDeletionDate(status.scheduled_for)}</strong>{" "}
              ({formatDaysLeft(status.days_left)} from now). Until then your account works as usual.
              Changed your mind? Cancel the deletion and nothing is lost. We have also emailed you a
              cancel link.
            </p>
            {error && (
              <p role="alert" className="t-small" style={{ margin: 0, color: "var(--c-danger, #b3261e)" }}>
                {error}
              </p>
            )}
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy}
              onClick={() => { void handleCancel(); }}
              style={{ alignSelf: "flex-start" }}
            >
              {busy ? "Cancelling…" : "Cancel the deletion"}
            </button>
          </section>
        ) : statusFailed ? (
          <p role="alert" className="t-small t-mute" style={{ margin: 0 }}>
            We couldn&apos;t check your account&apos;s deletion status. Please reload the page, or email{" "}
            {SUPPORT_EMAIL}.
          </p>
        ) : status === null ? (
          <p className="t-small t-mute" role="status" style={{ margin: 0 }}>Loading…</p>
        ) : (
          <section className="stack-4">
            <h2 className="t-h3" style={{ margin: 0 }}>Are you sure?</h2>
            <p className="t-small" style={{ margin: 0 }}>
              Signed in as <strong>{email}</strong>. Your account and data will be permanently deleted{" "}
              {status.grace_days} days after you confirm. You can cancel any time before then. To
              confirm, type your email address below.
            </p>
            <label className="stack-2">
              <span className="t-label">Your email address</span>
              <input
                className="input"
                type="email"
                autoComplete="off"
                value={confirmation}
                onChange={(e) => { setConfirmation(e.target.value); }}
                placeholder={email}
              />
            </label>
            {error && (
              <p role="alert" className="t-small" style={{ margin: 0, color: "var(--c-danger, #b3261e)" }}>
                {error}
              </p>
            )}
            <button
              type="button"
              className="btn btn--primary"
              disabled={!confirmed || busy}
              onClick={() => { void handleSchedule(); }}
              style={{ alignSelf: "flex-start" }}
            >
              {busy ? "Scheduling…" : "Delete my account"}
            </button>
          </section>
        )
      ) : (
        <section className="stack-5">
          <div className="stack-3">
            <h2 className="t-h3" style={{ margin: 0 }}>Sign in to delete your account</h2>
            <p className="t-small t-mute" style={{ margin: 0 }}>
              The quickest way. We ask you to sign in first so that only you can delete your account.
            </p>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => { navigateTo("/login?next=%2Fdelete-account"); }}
              style={{ alignSelf: "flex-start" }}
            >
              Sign in to continue
            </button>
          </div>

          <hr className="rule" />

          <div className="stack-3">
            <h2 className="t-h3" style={{ margin: 0 }}>Can&apos;t sign in?</h2>
            {linkSent ? (
              <p className="t-small" role="status" style={{ margin: 0 }}>
                If an account exists for <strong>{linkSent}</strong>, we have emailed it a
                confirmation link. It works once and expires in 1 hour. Your account is scheduled for
                deletion only after you open the link and confirm. Nothing arrived? Check your spam folder.
              </p>
            ) : (
              <>
                <p className="t-small t-mute" style={{ margin: 0 }}>
                  Enter the email address of your account. If it exists, we send a confirmation link
                  to that address &mdash; only the owner of the mailbox can use it.
                </p>
                <label className="stack-2">
                  <span className="t-label">Account email</span>
                  <input
                    className="input"
                    type="email"
                    autoComplete="email"
                    value={linkEmail}
                    onChange={(e) => { setLinkEmail(e.target.value); }}
                    placeholder="you@example.com"
                  />
                </label>
                {linkError && (
                  <p role="alert" className="t-small" style={{ margin: 0, color: "var(--c-danger, #b3261e)" }}>
                    {linkError}
                  </p>
                )}
                <button
                  type="button"
                  className="btn"
                  disabled={!linkEmailValid || linkBusy}
                  onClick={() => { void handleRequestLink(); }}
                  style={{ alignSelf: "flex-start" }}
                >
                  {linkBusy ? "Sending…" : "Email me a confirmation link"}
                </button>
              </>
            )}
            <p className="t-small t-mute" style={{ margin: 0 }}>
              No access to that mailbox either? Write to{" "}
              <a className="link" href={`mailto:${SUPPORT_EMAIL}?subject=Account%20Deletion%20Request`}>
                {SUPPORT_EMAIL}
              </a>
              . We will check that you own the account before deleting anything.
            </p>
          </div>
        </section>
      )}
    </DeletionShell>
  );
}
